import React, { useState } from 'react';
import { X, Video, Image as ImageIcon } from 'lucide-react';
import ImageSelectorModal from './ImageSelectorModal';

interface VideoMetadata {
  id: string;
  title: string;
  description?: string;
  url: string;
  thumbnail_url?: string;
  duration?: number;
  size: number;
  format?: string;
  resolution?: string;
  metadata?: any;
}

interface VideoMetadataModalProps {
  video: VideoMetadata;
  onClose: () => void;
  onSave: (video: VideoMetadata, metadata: any) => void;
}

export default function VideoMetadataModal({ video, onClose, onSave }: VideoMetadataModalProps) {
  const [metadata, setMetadata] = useState({
    title: video.title || '',
    description: video.description || '',
    thumbnail_url: video.thumbnail_url || '',
    tags: (video.metadata?.tags || []).join(', '),
    category: video.metadata?.category || '',
    language: video.metadata?.language || '',
    customFields: Object.entries(video.metadata?.custom || {}).map(([key, value]) => ({
      key,
      value: value as string
    }))
  });
  const [showImageSelector, setShowImageSelector] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const updatedMetadata = {
      tags: metadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
      category: metadata.category,
      language: metadata.language,
      custom: Object.fromEntries(
        metadata.customFields
          .filter(field => field.key && field.value)
          .map(field => [field.key, field.value])
      )
    };

    onSave(video, {
      title: metadata.title,
      description: metadata.description,
      thumbnail_url: metadata.thumbnail_url, // Ensure thumbnail_url is included
      ...updatedMetadata
    });
  };

  const handleImageSelect = (imageHtml: string) => {
    // Extract the image URL from the HTML string
    const match = imageHtml.match(/src="([^"]+)"/);
    if (match) {
      setMetadata(prev => ({
        ...prev,
        thumbnail_url: match[1]
      }));
    }
    setShowImageSelector(false);
  };

  const addCustomField = () => {
    setMetadata(prev => ({
      ...prev,
      customFields: [...prev.customFields, { key: '', value: '' }]
    }));
  };

  const removeCustomField = (index: number) => {
    setMetadata(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  const updateCustomField = (index: number, field: 'key' | 'value', value: string) => {
    setMetadata(prev => ({
      ...prev,
      customFields: prev.customFields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Video Metadata</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Video Preview */}
          <div>
            <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-4">
              {metadata.thumbnail_url ? (
                <img 
                  src={metadata.thumbnail_url} 
                  alt={metadata.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Video className="h-12 w-12 text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-700">Thumbnail</span>
              <button
                type="button"
                onClick={() => setShowImageSelector(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                <ImageIcon className="h-4 w-4 mr-1.5" />
                Choose Image
              </button>
            </div>
            <div className="text-sm text-gray-500 space-y-2">
              <p><strong>Format:</strong> {video.format || 'Unknown'}</p>
              <p><strong>Resolution:</strong> {video.resolution || 'Unknown'}</p>
              <p><strong>Size:</strong> {(video.size / (1024 * 1024)).toFixed(2)} MB</p>
              {video.duration && (
                <p><strong>Duration:</strong> {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</p>
              )}
              <div className="flex">
                <strong className="flex-shrink-0">URL:</strong>
                <div className="ml-1 break-all">
                  {video.url}
                </div>
              </div>
            </div>
          </div>

          {/* Metadata Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={metadata.title}
                onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                value={metadata.description}
                onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
              />
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                Tags
              </label>
              <input
                type="text"
                id="tags"
                value={metadata.tags}
                onChange={(e) => setMetadata({ ...metadata, tags: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Add tags separated by commas"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <input
                type="text"
                id="category"
                value={metadata.category}
                onChange={(e) => setMetadata({ ...metadata, category: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
              />
            </div>

            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                Language
              </label>
              <input
                type="text"
                id="language"
                value={metadata.language}
                onChange={(e) => setMetadata({ ...metadata, language: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Custom Fields
                </label>
                <button
                  type="button"
                  onClick={addCustomField}
                  className="text-sm text-[#F98B3D] hover:text-[#e07a2c]"
                >
                  + Add Field
                </button>
              </div>
              <div className="space-y-2">
                {metadata.customFields.map((field, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateCustomField(index, 'key', e.target.value)}
                      placeholder="Field name"
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                      placeholder="Field value"
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomField(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>

        <ImageSelectorModal
          isOpen={showImageSelector}
          onClose={() => setShowImageSelector(false)}
          onSelect={handleImageSelect}
        />
      </div>
    </div>
  );
}